import { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'

import {
  profileUpdateSchema,
  ProfileUpdateFormData,
  changePasswordSchema,
  ChangePasswordFormData,
} from '@/schemas/auth'
import { authService, ApiError } from '@/services/auth'
import { useAuthStore } from '@/stores/auth-store'
import { useOrganizationStore } from '@/stores/organization-store'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export function ProfilePage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const { user, updateUser, logout } = useAuthStore()
  const { clearContext } = useOrganizationStore()

  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)
  const [isProfileLoading, setIsProfileLoading] = useState(false)

  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null)
  const [isPasswordLoading, setIsPasswordLoading] = useState(false)
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false)

  const LANGUAGES = [
    { value: 'pt-BR', label: t('profile.languages.pt-BR') },
    { value: 'es', label: t('profile.languages.es') },
    { value: 'en', label: t('profile.languages.en') },
  ]

  const THEMES = [
    { value: 'dark', label: t('profile.themes.dark') },
    { value: 'light', label: t('profile.themes.light') },
    { value: 'system', label: t('profile.themes.system') },
  ]

  // Handle redirect after password change with proper cleanup
  useEffect(() => {
    if (passwordChangeSuccess) {
      const timer = setTimeout(() => {
        clearContext()
        logout()
        navigate('/login')
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [passwordChangeSuccess, clearContext, logout, navigate])

  const validLanguages = ['pt-BR', 'es', 'en'] as const
  const validThemes = ['dark', 'light', 'system'] as const

  const profileForm = useForm<ProfileUpdateFormData>({
    resolver: zodResolver(profileUpdateSchema),
    defaultValues: {
      full_name: user?.full_name || '',
      avatar_url: user?.avatar_url || '',
      language:
        validLanguages.find((lang) => lang === user?.settings?.language) || 'pt-BR',
      theme: validThemes.find((theme) => theme === user?.settings?.theme) || 'dark',
    },
  })

  const passwordForm = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
  })

  const onProfileSubmit = async (data: ProfileUpdateFormData) => {
    setProfileError(null)
    setProfileSuccess(null)
    setIsProfileLoading(true)

    try {
      const response = await authService.updateProfile({
        full_name: data.full_name,
        avatar_url: data.avatar_url || null,
        language: data.language,
        theme: data.theme,
      })

      updateUser({
        full_name: response.profile.full_name,
        avatar_url: response.profile.avatar_url,
        settings: response.profile.settings,
      })

      // Update i18n language if changed
      if (data.language && data.language !== i18n.language) {
        await i18n.changeLanguage(data.language)
      }

      setProfileSuccess(t('profile.profileUpdated'))
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>
      setProfileError(
        axiosError.response?.data?.detail || t('profile.updateError')
      )
    } finally {
      setIsProfileLoading(false)
    }
  }

  const onPasswordSubmit = async (data: ChangePasswordFormData) => {
    setPasswordError(null)
    setPasswordSuccess(null)
    setIsPasswordLoading(true)

    try {
      await authService.changePassword({
        current_password: data.current_password,
        new_password: data.new_password,
        new_password_confirm: data.new_password_confirm,
      })

      setPasswordSuccess(t('profile.passwordChanged'))
      passwordForm.reset()
      setPasswordChangeSuccess(true)
    } catch (err) {
      const axiosError = err as AxiosError<ApiError>
      setPasswordError(
        axiosError.response?.data?.detail || t('profile.changePasswordError')
      )
    } finally {
      setIsPasswordLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold">{t('profile.title')}</h1>
        <p className="text-muted-foreground">
          {t('profile.subtitle')}
        </p>
      </div>

      {/* Profile Form */}
      <Card>
          <CardHeader>
            <CardTitle>{t('profile.personalInfo')}</CardTitle>
            <CardDescription>
              {t('profile.personalInfoDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-4">
              {profileError && (
                <Alert variant="destructive">
                  <AlertDescription>{profileError}</AlertDescription>
                </Alert>
              )}

              {profileSuccess && (
                <Alert>
                  <AlertDescription className="text-green-600">
                    {profileSuccess}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">{t('profile.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  {t('profile.emailCannotChange')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_name">{t('profile.fullName')}</Label>
                <Input
                  id="full_name"
                  {...profileForm.register('full_name')}
                  placeholder={t('profile.fullNamePlaceholder')}
                />
                {profileForm.formState.errors.full_name && (
                  <p className="text-sm text-destructive">
                    {profileForm.formState.errors.full_name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="avatar_url">{t('profile.avatarUrl')}</Label>
                <Input
                  id="avatar_url"
                  {...profileForm.register('avatar_url')}
                  placeholder={t('profile.avatarUrlPlaceholder')}
                />
                {profileForm.formState.errors.avatar_url && (
                  <p className="text-sm text-destructive">
                    {profileForm.formState.errors.avatar_url.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">{t('profile.language')}</Label>
                <Controller
                  name="language"
                  control={profileForm.control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="language">
                        <SelectValue placeholder={t('profile.languageSelect')} />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="theme">{t('profile.theme')}</Label>
                <Controller
                  name="theme"
                  control={profileForm.control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger id="theme">
                        <SelectValue placeholder={t('profile.themeSelect')} />
                      </SelectTrigger>
                      <SelectContent>
                        {THEMES.map((theme) => (
                          <SelectItem key={theme.value} value={theme.value}>
                            {theme.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  {t('profile.themeNote')}
                </p>
              </div>

              <Button type="submit" disabled={isProfileLoading}>
                {isProfileLoading ? t('profile.saving') : t('profile.saveChanges')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Password Form */}
        <Card>
          <CardHeader>
            <CardTitle>{t('profile.changePassword')}</CardTitle>
            <CardDescription>
              {t('profile.changePasswordDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-4">
              {passwordError && (
                <Alert variant="destructive">
                  <AlertDescription>{passwordError}</AlertDescription>
                </Alert>
              )}

              {passwordSuccess && (
                <Alert>
                  <AlertDescription className="text-green-600">
                    {passwordSuccess}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="current_password">{t('profile.currentPassword')}</Label>
                <Input
                  id="current_password"
                  type="password"
                  {...passwordForm.register('current_password')}
                  placeholder={t('profile.currentPasswordPlaceholder')}
                />
                {passwordForm.formState.errors.current_password && (
                  <p className="text-sm text-destructive">
                    {passwordForm.formState.errors.current_password.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_password">{t('profile.newPassword')}</Label>
                <Input
                  id="new_password"
                  type="password"
                  {...passwordForm.register('new_password')}
                  placeholder={t('profile.newPasswordPlaceholder')}
                />
                {passwordForm.formState.errors.new_password && (
                  <p className="text-sm text-destructive">
                    {passwordForm.formState.errors.new_password.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('profile.passwordRequirements')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new_password_confirm">{t('profile.confirmNewPassword')}</Label>
                <Input
                  id="new_password_confirm"
                  type="password"
                  {...passwordForm.register('new_password_confirm')}
                  placeholder={t('profile.confirmNewPasswordPlaceholder')}
                />
                {passwordForm.formState.errors.new_password_confirm && (
                  <p className="text-sm text-destructive">
                    {passwordForm.formState.errors.new_password_confirm.message}
                  </p>
                )}
              </div>

              <Button type="submit" variant="destructive" disabled={isPasswordLoading}>
                {isPasswordLoading ? t('profile.changing') : t('profile.changePassword')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* 2FA Section */}
        <Card>
          <CardHeader>
            <CardTitle>{t('profile.twoFactorAuth')}</CardTitle>
            <CardDescription>
              {user?.two_factor_enabled
                ? t('profile.twoFactorEnabled')
                : t('profile.twoFactorDisabled')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">
                  {t('profile.twoFactorStatus')}: {user?.two_factor_enabled ? t('profile.twoFactorActive') : t('profile.twoFactorInactive')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {user?.two_factor_enabled
                    ? t('profile.twoFactorEnabledDescription')
                    : t('profile.twoFactorDisabledDescription')}
                </p>
              </div>
              <Button
                variant={user?.two_factor_enabled ? 'destructive' : 'default'}
                onClick={() => {
                  // TODO: Implement 2FA toggle modal
                  alert('Funcionalidade de toggle 2FA sera implementada em breve')
                }}
              >
                {user?.two_factor_enabled ? t('profile.disableTwoFactor') : t('profile.enableTwoFactor')}
              </Button>
            </div>
          </CardContent>
        </Card>
    </div>
  )
}
